import {useAui} from '@assistant-ui/react';
import {useLayoutEffect, useRef} from 'react';

import type {ActionResult} from '../../agentChat/actionResult';
import {
  draftKey,
  type SelectedPanel,
  type SelectedRun,
  useDraftStore,
} from '../../agentChat/composer';
import type {WBAgentController} from '../../agentChat/controller';
import {wbAgentControllerStore} from '../../agentChat/controller';
import {
  isUserRepresentable,
  toUserMessageContent,
} from '../../agentChat/messageConversions';
import type {AgentChatStore} from '../../agentChat/store';
import {
  hasMeaningfulContent,
  selectActiveThread,
  useAgentChatStore,
} from '../../agentChat/store';
import {selectIsOnNewChat} from '../../agentChat/store/selectors';
import type {ContentPart} from '../../agentChat/store/thread';
import {isAgentChatReadonly} from '../../agentChat/useAgentChatReadonly';
import {closeCustomizationSection} from './customizations/useCustomizationsStore';
import {useTrackWBAgentComposer} from './useTrackWBAgent';
import {useWBAgentWindowStore} from './window/useWBAgentWindowStore';

type Aui = ReturnType<typeof useAui>;
type TrackSent = ReturnType<typeof useTrackWBAgentComposer>['trackMessageSent'];

/**
 * WBAgentControllerBridge registers the imperative controller handle while the
 * agent chat is mounted, so buttons anywhere in the app can open the chat or
 * send a message. It must render inside `AuiProvider` (it reads `useAui`) and
 * is mounted only when the feature is available; the handle is cleared on
 * unmount. Renders nothing.
 */
export const WBAgentControllerBridge = (): null => {
  const aui = useAui();
  const store = useAgentChatStore();
  const {trackMessageSent} = useTrackWBAgentComposer();
  // NOTE: The ref keeps the tracker out of the effect deps, so the handle
  // re-registers only on `aui` changes.
  const trackRef = useRef(trackMessageSent);
  trackRef.current = trackMessageSent;

  // NOTE: Layout effect clears the handle before paint, beating any click that
  // would otherwise reach a handle whose runtime is being torn down.
  useLayoutEffect(
    () =>
      wbAgentControllerStore.registerHandle(
        createController(aui, store, () => trackRef.current)
      ),
    [aui, store]
  );

  return null;
};

function createController(
  aui: Aui,
  store: AgentChatStore,
  getTrackSent: () => TrackSent
): WBAgentController {
  const openChat = () => {
    useWBAgentWindowStore.getState().setOpen(true);
    closeCustomizationSection();
  };
  const addToChatDraft = (
    add: (key: string) => void
  ): ActionResult<'readonly'> => {
    if (isAgentChatReadonly()) {
      return {ok: false, reason: 'readonly'};
    }
    const state = store.getState();
    const shouldStartFresh =
      !useWBAgentWindowStore.getState().open && !selectIsOnNewChat(state);
    if (shouldStartFresh) {
      aui.threads().switchToNewThread();
    }
    const threadId = selectActiveThread(store.getState())?.id ?? null;
    add(draftKey(threadId));
    return {ok: true};
  };

  return {
    addPanelToChat(panel: SelectedPanel) {
      return addToChatDraft(key =>
        useDraftStore.getState().addPanel(key, panel)
      );
    },
    addRunToChat(run: SelectedRun) {
      return addToChatDraft(key => useDraftStore.getState().addRun(key, run));
    },
    startChat(prompt, {attachments, submit}) {
      if (isAgentChatReadonly()) {
        return {ok: false, reason: 'readonly'};
      }

      const parts = messageParts(prompt, attachments);
      if (!hasMeaningfulContent(parts.filter(isUserRepresentable))) {
        return {ok: false, reason: 'empty'};
      }

      aui.threads().switchToNewThread();
      if (submit) {
        aui.thread().append({
          role: 'user',
          content: toUserMessageContent(parts),
        });
        getTrackSent()('programmatic');
        openChat();
        return {ok: true};
      }

      const threadId = selectActiveThread(store.getState())?.id ?? null;
      const committed = useDraftStore
        .getState()
        .setDraftParts(draftKey(threadId), parts);
      openChat();
      return committed ? {ok: true} : {ok: false, reason: 'occupied'};
    },
    openChat,
    openChatToThread(threadId: string) {
      aui.threads().switchToThread(threadId);
      openChat();
    },
  };
}

// Normalize the two prompt shapes and prepend optional structured context.
function messageParts(
  prompt: string | ContentPart[],
  attachments: ContentPart[] = []
): ContentPart[] {
  if (typeof prompt !== 'string') {
    return [...attachments, ...prompt];
  }
  const text = prompt.trim();
  return text.length > 0
    ? [...attachments, {type: 'text', text, status: 'complete'}]
    : attachments;
}
